import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { NAV_LINKS } from "@/data/content";
import { POLICY_LINKS } from "@/data/policies";

const Wordmark = () => (
  <Link to="/" data-testid="logo-home-link" className="flex items-baseline gap-2 focus:outline-none focus:ring-2 focus:ring-sage rounded">
    <span className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-sage">UNSTUCK</span>
    <span className="hidden sm:inline text-xs uppercase tracking-widest font-semibold text-gold-dark whitespace-nowrap">15-minute activity decks</span>
  </Link>
);

const Layout = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 bg-cream/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between py-4 gap-4">
          <Wordmark />
          <nav data-testid="main-nav" className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    isActive ? "bg-sage text-white" : "text-ink hover:bg-sage-light"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-button"
                  aria-label="Open menu"
                  className="h-12 w-12 inline-flex items-center justify-center rounded-full border-2 border-sage text-sage hover:bg-sage hover:text-white transition-colors"
                >
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-cream w-80">
                <SheetTitle className="font-heading text-sage text-xl mb-4">UNSTUCK</SheetTitle>
                <nav className="flex flex-col gap-2">
                  {NAV_LINKS.map((link) => (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      end={link.to === "/"}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={({ isActive }) =>
                        `px-4 py-3 rounded-xl text-lg font-medium transition-colors ${
                          isActive ? "bg-sage text-white" : "text-ink hover:bg-sage-light"
                        }`
                      }
                    >
                      {link.label}
                    </NavLink>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-sage-dark text-cream mt-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-14 grid gap-10 md:grid-cols-3">
          <div>
            <p className="font-heading font-black text-2xl mb-3">UNSTUCK</p>
            <p className="text-cream/80 text-base leading-relaxed max-w-xs">
              Simple 15-minute activity decks that help people take one small step and build momentum — one manageable action at a time.
            </p>
          </div>
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-gold mb-4">Shop</p>
            <ul className="space-y-2 text-base">
              <li><Link data-testid="footer-seniors-link" className="hover:text-gold transition-colors" to="/seniors">UNSTUCK Seniors</Link></li>
              <li><Link data-testid="footer-deck-link" className="hover:text-gold transition-colors" to="/shop/seniors-deck">Individual Seniors Deck</Link></li>
              <li><Link data-testid="footer-collections-link" className="hover:text-gold transition-colors" to="/collections">Explore UNSTUCK Collections</Link></li>
              <li><Link data-testid="footer-organizations-link" className="hover:text-gold transition-colors" to="/organizations">Organizations &amp; Bulk Orders</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-gold mb-4">Help</p>
            <ul className="space-y-2 text-base">
              <li><Link data-testid="footer-faq-link" className="hover:text-gold transition-colors" to="/faq">FAQ</Link></li>
              <li><Link data-testid="footer-contact-link" className="hover:text-gold transition-colors" to="/contact">Contact</Link></li>
              <li><a data-testid="footer-email-link" className="hover:text-gold transition-colors" href="mailto:Unstuckbytammi@gmail.com">Unstuckbytammi@gmail.com</a></li>
            </ul>
            <p className="text-sm uppercase tracking-widest font-semibold text-gold mt-8 mb-4">Policies</p>
            <ul className="space-y-2 text-base">
              {POLICY_LINKS.map((p) => (
                <li key={p.to}>
                  <Link data-testid={`footer-policy-${p.to.split("/").pop()}`} className="hover:text-gold transition-colors" to={p.to}>{p.label}</Link>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-cream/70 leading-relaxed">
              UNSTUCK decks are everyday activity cards. They are not medical treatment, therapy, or a cure.
            </p>
          </div>
        </div>
        <div className="border-t border-cream/15">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-5 text-sm text-cream/70">
            © {new Date().getFullYear()} UNSTUCK. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
